<template>
  <div>
    <v-dialog v-model="show" max-width="1300px" scrollable :persistent="persistent">
      <v-card>
        <v-card-text>
          <v-row class="pt-4">
            <v-col cols="12" sm="6" lg="5" class="d-flex justify-center align-center">
              <v-img max-width="100%" :src="getImagePath(image)" class="grey lighten-2" contain></v-img>
            </v-col>
            <v-col cols="12" sm="6" lg="7" class="pl-5 position-relative">
              <div class="position-absolute right-0">
                <v-chip class="primary" v-if="image.show_new_product_badge">{{
                  $t('components.dialog.imageDetails.newProduct')
                }}</v-chip>
              </div>

              <h3 class="mt-3">{{ $t('components.dialog.imageDetails.description') }}</h3>
              <div class="mb-2">
                <span class="text-with-linebreak">{{ localeDescription }}</span>
              </div>

              <div class="mb-2" v-if="image.is_new_product && image.release_date">
                <h3>{{ $t('components.dialog.imageDetails.launchDate') }}</h3>
                <div>{{ image.release_date }}</div>
              </div>

              <h3>{{ $t('components.dialog.imageDetails.season') }}</h3>
              <div class="mb-2">
                {{ seasonText ? seasonText : '--' }}
              </div>

              <h3>{{ $t('components.dialog.imageDetails.price') }}</h3>
              <div class="mb-2">
                {{ localePrice }}
              </div>

              <h3>{{ $t('components.dialog.imageDetails.externalUrl') }}</h3>
              <div class="mb-2" v-html="externalUrl"></div>

              <h3>{{ $t('components.dialog.imageDetails.company') }}</h3>
              <div class="mb-2">
                {{ companyName }}<br />
                {{ $t('components.dialog.imageDetails.phone') }}: {{ companyPhone ? companyPhone : '--' }}<br />
                {{ $t('components.dialog.imageDetails.email') }}: {{ companyEmail ? companyEmail : '--' }}
              </div>

              <h3>{{ $t('components.dialog.imageDetails.imageInformation') }}</h3>
              <div class="mb-2">
                {{ $t('components.dialog.imageDetails.dimensions') }}: {{ image.width }} x {{ image.height }}<br />
                {{ $t('components.dialog.imageDetails.imageAddedAt') }}: {{ image.create_date | formatDate }}
              </div>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions class="pr-4 pb-3">
          <v-spacer></v-spacer>
          <v-btn text @click="close()"> {{ $t('common.close') }} </v-btn>
          <v-tooltip top :disabled="!isAuthenticated || (isAuthenticatedJournalist && isCountryMatch(image))">
            <template v-slot:activator="{ on, attr }">
              <div v-on="on" v-bind="attr" class="ml-3">
                <v-btn
                  color="primary"
                  class="black--text"
                  @click="downloadImage(image)"
                  :disabled="!enabledDownloadButton"
                >
                  {{ $t('components.dialog.imageDetails.download') }}
                </v-btn>
              </div>
            </template>
            <span v-if="isAuthenticated && !isAuthenticatedJournalist">{{
              $t('components.dialog.imageDetails.onlyJournalistTooltip')
            }}</span>
            <span v-else>{{ $t('components.dialog.imageDetails.missingCountryTooltip') }} </span>
          </v-tooltip>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <login-dialog-component
      :visible="showLoginDialog"
      :download="true"
      @success="afterLoginSuccess()"
      @close="showLoginDialog = false"
    ></login-dialog-component>

    <info-dialog-component v-model="showMissingCountryInfo" :title="$t('components.dialog.imageDetails.missingCountryInfoTitle')">
      <p>{{ $t('components.dialog.imageDetails.missingCountryInfoContent') }} {{ countriesForSelectedImage }}</p>
    </info-dialog-component>
    <info-dialog-component v-model="showNotJournalistInfo" :title="$t('components.dialog.imageDetails.onlyJournalistInfoTitle')">
      <p>{{ $t('components.dialog.imageDetails.onlyJournalistInfoContent') }}</p>
    </info-dialog-component>
  </div>
</template>

<script>
import { FileService } from '../../services/file.service';
import { mapStores } from 'pinia';
import { useUserStore } from '../../stores/UserStore';
import { useLookupStore } from '../../stores/LookupStore';
import { isSwedishLocale, pickLocaleText } from '../../services/locale.service';
import LoginDialogComponent from './LoginDialogComponent.vue';
import InfoDialogComponent from '@shared/components/InfoDialogComponent.vue';

export default {
  components: {
    LoginDialogComponent,
    InfoDialogComponent,
  },

  props: {
    visible: Boolean,
    persistent: {
      type: Boolean,
      default: () => {
        return false;
      },
    },
    image: {
      type: Object,
      default: () => {
        return {};
      },
    },
  },

  model: {
    prop: 'visible',
    event: 'update',
  },

  data() {
    return {
      showLoginDialog: false,
      showMissingCountryInfo: false,
      showNotJournalistInfo: false,
      lastSelectedImage: undefined,
    };
  },

  watch: {
    visible: function (newValue, oldValue) {
      if (newValue === true) {
        this.logOpen();
      }
    },
  },

  computed: {
    ...mapStores(useUserStore, useLookupStore),

    show: {
      get: function () {
        return this.visible;
      },
      set: function (value) {
        this.$emit('update', value);

        if (value === false) {
          this.$emit('close');
        } else {
          this.logOpen();
        }
      },
    },

    isAuthenticated() {
      return this.userStore.isAuthenticated;
    },

    isAuthenticatedJournalist() {
      return this.userStore.isAuthenticatedJournalist;
    },

    enabledDownloadButton() {
      return !this.isAuthenticated || (this.isAuthenticatedJournalist && this.isCountryMatch(this.image));
    },

    companyName: function () {
      return this.image && this.image.company ? this.image.company.name : '';
    },

    companyPhone: function () {
      return this.image && this.image.company ? this.image.company.phone : '';
    },

    companyEmail: function () {
      return this.image && this.image.company ? this.image.company.email : '';
    },

    seasonText: function () {
      return pickLocaleText(this.image?.season);
    },

    externalUrl: function () {
      const linkText = this.$i18n.t('components.dialog.imageDetails.clickHereToOpenExternalUrl');
      return this.image && this.image.external_url
        ? `<a href="${this.image.external_url}" target="_blank">${linkText}</a>`
        : ' -- ';
    },

    countriesForSelectedImage() {
      if (!this.image?.company?.countries) return '';
      return this.lookupStore.countryNamesByIds(this.image?.company?.countries);
    },

    localeDescription() {
      const swedishDescription = this.image?.description;
      const englishDescription = this.image?.description_eng;
      
      if (!swedishDescription && !englishDescription) {
        return '--';
      }

      if (isSwedishLocale()) {
        return swedishDescription || englishDescription;
      }
      // pick swedish description if english is not available, so that we always have a description
      return englishDescription || swedishDescription;
    },

    localePrice() {
      const swedishPrice = this.image?.price;
      const englishPrice = this.image?.price_eng;
      
      if (!swedishPrice && !englishPrice) {
        return '--';
      }

      if (isSwedishLocale()) {
        return swedishPrice || englishPrice;
      }
      // pick swedish price if english is not available, so that we always have a price
      return englishPrice || swedishPrice;
    },
  },

  methods: {
    getImagePath(image) {
      if (!image.thumb_name) {
        return '';
      }
      // return image.path + '/' + image.thumb_name;
      return image.thumbnailPath;
    },

    close() {
      this.show = false;
    },

    downloadImage(image) {
      if (this.userStore.isAuthenticated) {
        if (!image) {
          image = this.lastSelectedImage;
        }
        const rnd = Math.floor(Math.random() * 100000000);
        const win = window.open(`${API_URL}/image/download/${image.id}/${rnd}`, '_blank');
        this.lastSelectedImage = undefined;
      } else {
        this.lastSelectedImage = image;
        this.showLoginDialog = true;
      }
    },

    afterLoginSuccess() {
      if (this.isAuthenticatedJournalist && this.isCountryMatch(this.lastSelectedImage)) {
        this.downloadImage();
        return;
      }

      if (!this.isAuthenticatedJournalist) {
        this.showNotJournalistInfo = true;
        return;
      }

      // show info that user is not allowed to download image due to country restrictions
      this.showMissingCountryInfo = true;
    },

    logOpen() {
      new FileService().logImageClick(this.image);
    },

    isCountryMatch(image) {
      return this.userStore.hasMatchingCountry(image.company?.countries);
    },
  },
};
</script>

<style scoped>
.image {
  max-width: 400px;
  max-height: 400px;
}

.text-with-linebreak {
  white-space: pre-line;
}
</style>
